import { useState } from 'react';
import { useKnowledgeBases } from '../context/KnowledgeBaseContext';
import { CreateKnowledgeBaseModal } from '../components/CreateKnowledgeBaseModal';
import { KnowledgeBaseDetail } from '../components/KnowledgeBaseDetail';

export function Upload() {
  const { knowledgeBases, loading, error } = useKnowledgeBases();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedKBId, setSelectedKBId] = useState<string | null>(null);

  if (selectedKBId) {
    return (
      <KnowledgeBaseDetail
        kbId={selectedKBId}
        onBack={() => setSelectedKBId(null)}
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-neutral-800 font-heading">Upload</h1>
        <p className="text-neutral-500 text-sm mt-1">Manage knowledge bases for all of your tools and assistants.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-btn text-xs text-red-700" role="alert">
          {error}
        </div>
      )}

      {loading && knowledgeBases.length === 0 ? (
        <div className="text-center py-12 text-neutral-400">Loading knowledge bases...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Create New card */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="card border-2 border-dashed border-neutral-300 hover:border-primary-400 hover:bg-primary-50/50 flex flex-col items-center justify-center py-10 transition-colors group"
          >
            <div className="w-10 h-10 rounded-full bg-neutral-100 group-hover:bg-primary-100 flex items-center justify-center mb-3 transition-colors">
              <svg className="w-5 h-5 text-neutral-400 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="text-sm font-medium text-neutral-500 group-hover:text-primary-600">New Knowledge Base</span>
          </button>

          {/* Existing knowledge bases */}
          {knowledgeBases.map(kb => {
            const statusClass =
              kb.status === 'ready' ? 'badge-green' :
              kb.status === 'error' ? 'badge-red' : 'badge-yellow';

            return (
              <div key={kb.id} className="card p-5 flex flex-col gap-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-neutral-800 truncate">{kb.name}</h3>
                    {kb.description && (
                      <p className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{kb.description}</p>
                    )}
                  </div>
                  <span className={`badge ${statusClass} ml-2 flex-shrink-0`}>
                    {kb.status}
                  </span>
                </div>

                <div className="text-xs text-neutral-500">
                  {kb.file_count} {kb.file_count === 1 ? 'file' : 'files'}
                </div>

                {kb.assistant_count !== undefined && (
                  <div className="text-xs text-neutral-400">
                    Used by {kb.assistant_count} {kb.assistant_count === 1 ? 'assistant' : 'assistants'}
                  </div>
                )}

                <div className="flex gap-2 mt-auto pt-2 border-t border-neutral-100">
                  <button
                    onClick={() => setSelectedKBId(kb.id)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Manage
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateKnowledgeBaseModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}
